import React, { FC, HTMLAttributes, ReactNode } from "react";
import Image from "next/image";
import clsx from "clsx";
import { ArrowRight } from "react-feather";

import ActionButton from "../atoms/buttons/ActionButton";
import icon, { IIconName } from "@/utils/icon.helper";
import { ITextButton } from "../atoms/buttons/types";
import HtmlText from "@/sections/common/HtmlText";

// ----------------------------------------------------------------------------
// IndustryCard — used by IndustryCardSection's horizontal accordion.
//
// Two states:
//   - INACTIVE: minimal — icon + name + a small chevron hint
//   - ACTIVE:   reveals the description + CTA, takes 2x the width
//
// Style polish (vs the previous version):
//   - Canonical Bricolage Grotesque + clamp typography (no more text-h5/h6)
//   - Light font weight on headings (matches the rest of the redesigned
//     sections we shipped today)
//   - Orange CTA button via CTAButton — same family as the home hero
//   - Cleaner chevron pill, no dated dividers
// ----------------------------------------------------------------------------

export type IIndustryCardContent = {
  icon?: IIconName;
  heading?: string;
  description?: ReactNode;
  button?: ITextButton;
};

export type IIndustryCardProps = HTMLAttributes<HTMLDivElement> &
  IIndustryCardContent & {
    isActive?: boolean;
    openCard?: () => void;
  };

// Derives the industry overview page URL from the card heading.
// "Healthcare" → "/industries/healthcare", "SaaS" → "/industries/saas", etc.
// Matches the Strapi industry slugs exactly (verified against the live
// /industries endpoint — healthcare, fintech, saas, manufacturing).
const industryHrefFor = (heading?: string): string | undefined => {
  if (!heading) return undefined;
  const slug = heading.trim().toLowerCase().replace(/\s+/g, "-");
  return `/industries/${slug}`;
};

const IndustryCard: FC<IIndustryCardProps> = ({
  icon: cardIcon,
  heading,
  description,
  button,
  isActive,
  openCard,
  className,
  ...other
}) => {
  if (!cardIcon && !heading && !description && !button) {
    return null;
  }

  const industryHref = industryHrefFor(heading);

  return (
    <div
      className={clsx(
        // Base card shape — chrome (bg, ring, shadow) is supplied by
        // the parent IndustryCardSection.
        //
        // Responsive heights:
        //   - below lg: every card is fully expanded (description + CTA
        //     shown). max-h-none lets the content size the card naturally,
        //     so the grid cells stay aligned.
        //   - lg+: accordion behavior — inactive cards collapse to icon +
        //     heading only (max-h limited), active card expands to show
        //     description + CTA.
        //
        // Cursor: pointer ONLY on lg+ when inactive (where clicking
        // promotes the card to active). Below lg every card is already
        // expanded, so no cursor-pointer needed.
        "relative flex w-full flex-col gap-5 overflow-hidden p-5 transition-all duration-700 ease-out lg:gap-6 lg:p-7",
        // Heights
        "lg:!max-h-full lg:min-h-[260px]",
        isActive ? "lg:max-h-[340px]" : "lg:max-h-44",
        // Cursor
        isActive
          ? "cursor-default"
          : "cursor-default lg:cursor-pointer",
        className,
      )}
      onClick={() => !isActive && openCard && openCard()}
      {...other}
    >
      {/* Icon + heading row. The heading slide-up animation ONLY runs on
          lg+ where the accordion behavior is in play — below lg every
          card is already showing its description, so the heading stays
          where it is. */}
      <div className="relative">
        {cardIcon && (
          <Image
            src={icon(cardIcon)}
            alt={cardIcon}
            height={48}
            width={48}
            className={clsx(
              "size-10 transition-transform duration-700 ease-out xl:size-[50px]",
              heading && "mb-4 lg:mb-6",
            )}
          />
        )}

        {heading && (
          <h3
            className={clsx(
              "break-words font-bricolage-grotesque text-[clamp(1.125rem,0.75vw+0.875rem,1.5rem)] font-extralight tracking-tight text-black-950 transition-all duration-700 ease-out 2xl:break-normal",
              // Single-line clamp only on lg+ (where accordion runs).
              // Below lg the heading is on its own row and can wrap.
              "lg:line-clamp-1",
              // On lg+, when active the heading slides up + right so
              // the description fits in the freed space below the icon.
              isActive &&
                "lg:ml-2 lg:-translate-y-[56px] lg:translate-x-10 xl:-translate-y-[68px] xl:translate-x-16",
            )}
          >
            {heading}
          </h3>
        )}
      </div>

      {/* Description + CTAs.
            - below lg: always visible (every card is expanded in the grid)
            - lg+: hidden via translate-x off-screen, slides in when active */}
      {(description || button || industryHref) && (
        <div
          className={clsx(
            "transition-all duration-700 ease-out",
            // Below lg: stays in place, always visible.
            // lg+: starts off-screen to the right and slides in only when
            //      active. -mt-10 pulls it up under the icon when active.
            "lg:-mt-10 lg:translate-x-[140%]",
            isActive && "lg:!translate-x-0",
          )}
        >
          {description && (
            // Below lg: no line-clamp — every card is expanded in the
            // grid so the full description must read end-to-end (per
            // user feedback: "make sure description fit so we don't
            // have to truncate content"). At lg+ the card is part of
            // an accordion with a fixed visible area, so we clamp
            // there to keep the layout tidy.
            <div className="mb-6 lg:line-clamp-3 lg:w-[280px] xl:w-[360px]">
              {/* Responsive weight — font-light on mobile (300 reads
                  too thin at 14px on phone screens) → font-extralight on lg+
                  where the airy editorial weight pairs well with the
                  card's wider visible area. */}
              <HtmlText className="prose-p:!text-[clamp(0.875rem,0.3vw+0.8rem,1rem)] prose-p:!font-light prose-p:!leading-relaxed prose-p:!text-black-700 lg:prose-p:!font-extralight">
                {description}
              </HtmlText>
            </div>
          )}

          {/* Action row — "Explore" (industry overview page) is a
              navigational action, not a conversion CTA, so it uses
              ActionButton in blue pill mode. "View case study" is even
              more secondary — renders as a text link with the arrow. */}
          {(industryHref || button?.link) && (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              {industryHref && (
                <ActionButton
                  id={`industry-card-${heading?.toLowerCase()}-explore`}
                  href={industryHref}
                  variant="primary"
                  size="sm"
                >
                  Explore
                </ActionButton>
              )}

              {button?.link && button?.text && (
                <ActionButton
                  id={button.id}
                  href={button.link}
                  target={button.target}
                  mode="link"
                >
                  {button.text}
                </ActionButton>
              )}
            </div>
          )}
        </div>
      )}

      {/* Inactive-state chevron hint — only relevant on lg+ where the
          accordion behavior runs. Hidden below lg (every card is already
          expanded in the grid, so no "click to reveal" cue needed). */}
      {openCard && (
        <div
          className={clsx(
            "absolute bottom-6 right-6 hidden h-9 w-9 items-center justify-center rounded-full bg-white/80 text-black-600 ring-1 ring-black-100 transition-all duration-500 ease-out lg:inline-flex",
            isActive && "translate-x-[150%] opacity-0",
          )}
          aria-hidden="true"
        >
          <ArrowRight size={14} />
        </div>
      )}
    </div>
  );
};

export default IndustryCard;
